import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { orgSchema } from './org-shared';
import { plainScheduleSchema } from './schedule';

export const schoolStatusSchema = z.enum(['active', 'inactive']);
export const SchoolStatus = schoolStatusSchema.enum;
export type SchoolStatus = z.infer<typeof schoolStatusSchema>;

export const schoolSchema = z.object({
  id: z.string(),
  type: z.literal('school'),
  name: z.string(),
  orgId: z.string(),
  schoolStatus: schoolStatusSchema,
  attendancePercentLast14Days: z.number().nullable(),
  totalStudents: z.number().int().nullable(),
  studentsInfoUpdatedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  totalFutureSessions: z.array(z.number().int()).nullable(),
  nextSessionPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  sessionInfoUpdatedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  address: z.string().nullable(),
  email: z.string().email().nullable(),
  needSpanishFeedback: z.boolean().nullable(),
  phone: z.string().nullable(),
  schoolAdminName: z.string().nullable(),
  totalSeats: z.number().int(),
  canSendSchoolParentReminders: z.boolean().nullable(),
  canSendFamilyParentReminders: z.boolean().nullable(),
});
export type School = z.infer<typeof schoolSchema>;

// Minimal school schema with only id, name, status used for APIs
// that are user-facing. Students/coaches should not see full school data like attendance percent,
// total students, etc.
export const minimalSchoolSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: schoolStatusSchema,
  orgId: z.string(),
});
export type MinimalSchool = z.infer<typeof minimalSchoolSchema>;

export const schoolWithOrgSchema = schoolSchema.extend({
  org: orgSchema,
});
export type SchoolWithOrg = z.infer<typeof schoolWithOrgSchema>;

export const minimalSchoolWithOrgSchema = minimalSchoolSchema.extend({
  org: orgSchema,
});
export type MinimalSchoolWithOrg = z.infer<typeof minimalSchoolWithOrgSchema>;

export const schoolWithOrgAndPlainScheduleSchema = schoolWithOrgSchema.extend({
  schedules: z.lazy(() => z.array(plainScheduleSchema())),
});
export type SchoolWithOrgAndPlainSchedule = z.infer<typeof schoolWithOrgAndPlainScheduleSchema>;
